import React, { useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowRight, FiMinus, FiPlus, FiShoppingBag, FiTrash2 } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/cartpage.scss";

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity } = useContext(CartContext);
  const [note, setNote] = useState("");

  const lines = useMemo(
    () =>
      cartItems
        .map((item) => ({ ...item, product: productData.find((product) => product.id === item.id) }))
        .filter((item) => item.product),
    [cartItems]
  );
  const subtotal = lines.reduce((total, item) => total + item.product.price * item.quantity, 0);
  const shipping = siteConfig.shipping.flatRate;

  if (lines.length === 0) {
    return (
      <main className="cart-empty">
        <FiShoppingBag aria-hidden="true" />
        <p className="eyebrow">Your bag is waiting</p>
        <h1>Your shopping bag is empty</h1>
        <p>Explore paper, pens and small desk companions chosen for everyday use.</p>
        <Link to="/shop" className="button">Continue shopping</Link>
      </main>
    );
  }

  return (
    <main className="cart-page page-shell">
      <div className="cart-page__heading">
        <p className="eyebrow">Your selection</p>
        <h1>Shopping bag</h1>
        <span>{lines.reduce((total, item) => total + item.quantity, 0)} items</span>
      </div>

      <div className="shipping-progress">
        <p><strong>Tracked delivery is {formatPrice(siteConfig.shipping.flatRate)}</strong> per order.</p>
        <div><span style={{ width: "100%" }} /></div>
      </div>

      <div className="cart-page__layout">
        <section className="cart-lines" aria-label="Items in shopping bag">
          <div className="cart-lines__head"><span>Product</span><span>Quantity</span><span>Total</span></div>
          {lines.map(({ product, quantity, size, color }) => (
            <article className="cart-line" key={`${product.id}-${size}-${color}`}>
              <Link className="cart-line__image" to={`/shopDetails/${product.id}`}>
                <img src={product.image} alt={product.name} width="240" height="240" />
              </Link>
              <div className="cart-line__details">
                <p>{product.brand}</p>
                <h2><Link to={`/shopDetails/${product.id}`}>{product.name}</Link></h2>
                <span>{color}{color && size ? " · " : ""}{size}</span>
                <span className="cart-line__unit">{formatPrice(product.price)} each</span>
                <button type="button" onClick={() => removeFromCart(product.id, size, color)}><FiTrash2 /> Remove</button>
              </div>
              <div className="cart-line__quantity quantity-picker">
                <button type="button" onClick={() => quantity === 1 ? removeFromCart(product.id, size, color) : updateQuantity(product.id, size, color, quantity - 1)} aria-label={`Decrease ${product.name} quantity`}><FiMinus /></button>
                <span>{quantity}</span>
                <button type="button" onClick={() => updateQuantity(product.id, size, color, quantity + 1)} aria-label={`Increase ${product.name} quantity`}><FiPlus /></button>
              </div>
              <strong className="cart-line__total">{formatPrice(product.price * quantity)}</strong>
            </article>
          ))}

          <div className="cart-note">
            <label htmlFor="order-note">A note for your order <span>(optional)</span></label>
            <textarea id="order-note" value={note} onChange={(event) => setNote(event.target.value)} placeholder="Gift note or packing request" rows="3" />
          </div>
          <Link className="text-link" to="/shop">Continue shopping <FiArrowRight /></Link>
        </section>

        <aside className="cart-summary">
          <h2>Order summary</h2>
          <dl>
            <div><dt>Subtotal</dt><dd>{formatPrice(subtotal)}</dd></div>
            <div><dt>Shipping</dt><dd>{shipping === 0 ? "Free" : formatPrice(shipping)}</dd></div>
            <div className="cart-summary__total"><dt>Total <small>{siteConfig.currency}</small></dt><dd>{formatPrice(subtotal + shipping)}</dd></div>
          </dl>
          <p>Taxes included. Final delivery details are confirmed at checkout.</p>
          <button type="button" className="button" onClick={() => navigate("/checkout", { state: { orderNote: note } })}>Checkout securely <FiArrowRight /></button>
          <div className="secure-note"><span>SSL</span> Secure checkout · Cards and pay later</div>
        </aside>
      </div>
    </main>
  );
};

export default CartPage;
